import { useState } from 'preact/hooks';
import { api } from '../api.js';
import { useStore } from '../store.js';
import { receipts, wallet } from '../wallet.js';
import { verifyAll, type VerifyReport } from '../../../src/verifier/verify.js';
import { zec } from '../format.js';
import type { LogEntry } from '../../../src/core/types.js';

export function Verify() {
  const s = useStore();
  const [progress, setProgress] = useState('');
  const [report, setReport] = useState<VerifyReport | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    setReport(null);
    try {
      const entries: LogEntry[] = [];
      for (let from = 0; ; ) {
        const page = await api.log(from, 5000);
        entries.push(...page.entries);
        from += page.entries.length;
        setProgress(`Downloaded ${entries.length} of ${page.seq + 1} entries…`);
        if (page.entries.length === 0 || from > page.seq) break;
      }
      const cps = await api.checkpoints();
      setProgress(`Replaying ${entries.length} entries…`);
      await new Promise((r) => setTimeout(r, 30));
      const r = await verifyAll(entries, cps, { expectSequencer: s.info?.sequencer, receipts: wallet() ? receipts() : [] });
      setReport(r);
      setProgress('');
    } catch (x) {
      setProgress(`Could not finish: ${(x as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="page-narrow">
      <h1 class="page-title">Check every trade yourself</h1>
      <p class="lede">
        Trades settle instantly on our sequencer instead of waiting for Zcash blocks. In exchange, everything the sequencer does is public and
        signed, so you don't have to take our word for anything.
      </p>
      <div class="steps">
        <div>Every action you take is signed by your own key. Nobody can trade or withdraw in your name.</div>
        <div>Every entry in the log is chained to the one before it and signed by the sequencer. Editing, removing or reordering anything breaks the chain.</div>
        <div>Anyone can replay the whole log with the same rules and must arrive at the same balances. A made-up result does not survive the replay.</div>
        <div>Snapshots of the state are written into Zcash transactions, so history cannot be quietly rewritten later.</div>
        <div>Your receipts prove what the sequencer told you. If it ever shows a different history, your receipts are the evidence.</div>
      </div>
      <button class="btn brass" onClick={run} disabled={busy}>
        {busy ? 'Checking…' : 'Replay the whole log in this browser'}
      </button>
      {progress && <p class="muted">{progress}</p>}
      {report && (
        <div class={`panel`} style={{ marginTop: 20, borderColor: report.ok ? 'var(--buy)' : 'var(--sell)' }}>
          <h2 class="display" style={{ fontSize: 34, color: report.ok ? 'var(--buy)' : 'var(--sell)' }}>
            {report.ok ? 'Everything checks out' : 'Problems found'}
          </h2>
          <table class="list" style={{ marginTop: 12 }}>
            <tbody>
              <tr>
                <td>Entries replayed</td>
                <td class="r">{report.stats.entries}</td>
              </tr>
              <tr>
                <td>Accounts</td>
                <td class="r">{report.stats.users}</td>
              </tr>
              <tr>
                <td>Coins</td>
                <td class="r">{report.stats.tokens}</td>
              </tr>
              <tr>
                <td>Signed snapshots checked</td>
                <td class="r">{report.stats.checkpoints}</td>
              </tr>
              <tr>
                <td>Your receipts checked</td>
                <td class="r">{wallet() ? receipts().length : 0}</td>
              </tr>
              <tr>
                <td>ZEC the platform owes its users</td>
                <td class="r">{zec(report.stats.liabilitiesZat, 8)} ZEC</td>
              </tr>
              <tr>
                <td>Latest hash</td>
                <td class="r" style={{ fontFamily: 'var(--mono)', fontSize: 12, wordBreak: 'break-all' }}>
                  {report.head}
                </td>
              </tr>
            </tbody>
          </table>
          {report.errors.map((e) => (
            <div class="notice bad" style={{ marginTop: 8 }} key={e}>
              {e}
            </div>
          ))}
        </div>
      )}
      <h2 class="display" style={{ fontSize: 32, margin: '40px 0 8px' }}>
        Going further
      </h2>
      <p class="muted">
        This page checks the log and the signatures. To also check every deposit, withdrawal and snapshot against the Zcash chain, and that the
        vault holds at least what it owes, run the open-source verifier with your own Zcash node and the vault's published viewing key:
      </p>
      <div class="address">npm run verify -- --url {location.origin} --rpc</div>
    </div>
  );
}
